import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import buddhistEra from 'dayjs/plugin/buddhistEra'
import _ from 'lodash'
import Resizer from 'react-image-file-resizer'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { isNumeric } from 'src/modules/ManageProspect/Form/events'
import { MONTH_TH_SHORT_LIST } from 'src/constants/calendar'
import { io } from 'socket.io-client'
import { tickQueue } from 'src/redux/slices/queue'

export const convertFormatDateTime = ({ value, type }) => {
  dayjs.extend(buddhistEra)
  dayjs.extend(utc)
  let result = '-'
  if (type === 'date') {
    result = dayjs(new Date(value))
      .utc()
      .format(window.__env__.REACT_APP_DATE_SHOW)
  }

  if (type === 'dateYYYY') {
    result = dayjs(new Date(value)).utc().format('DD/MM/YYYY')
  }

  if (type === 'dateDb') {
    result = dayjs(new Date(value)).format(window.__env__.REACT_APP_DATE_DB)
  }

  if (type === 'dateTime') {
    result =
      dayjs(new Date(value)).utc().format(window.__env__.REACT_APP_DATE_SHOW) +
      ' ' +
      dayjs(new Date(value)).utc().format(window.__env__.REACT_APP_DATE_TIME)
  }

  if (type === 'time') {
    result = dayjs(new Date(value))
      .utc()
      .format(window.__env__.REACT_APP_DATE_TIME)
  }

  if (type === 'timeWithSec') {
    result = dayjs(new Date(value)).utc().format('HH:mm:ss')
  }

  if (type === 'dayMonth') {
    result = dayjs(new Date(value))
      .utc()
      .format(window.__env__.REACT_APP_DATE_DAY_MONTH)
  }

  return result
}

export const isMediaWidthMoreThan = (breakpoint) => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.up(breakpoint))
}

export const isMediaWidth = (breakpoint) => {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.only(breakpoint))
}

export const getQueryParams = (field) => {
  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  return urlParams.get(field)
}

export const convertFormatTime = (data, isShort) => {
  const hours = _.get(data, 'hours', 0)
  const minutes = _.get(data, 'minutes', 0)
  const hourText = isShort ? 'ชม.' : 'ชั่วโมง'

  if (hours > 0 && minutes > 0) return `${hours} ${hourText} ${minutes} นาที`
  if (hours === 0) return `${minutes} นาที`
  if (minutes === 0) return `${hours} ${hourText}`
}

export const convertDateRange = (dateRange) => {
  const startDay = _.get(dateRange, 'startDateDay', '')
  const startMonth = _.get(dateRange, 'startDateMonth', '')
  const startYear = _.get(dateRange, 'startDateYear', '')
  const finishDate = _.get(dateRange, 'finishDate', '')
  const finishDay = _.get(dateRange, 'finishDateDay', '')
  const finishMonth = _.get(dateRange, 'finishDateMonth', '')
  const finishYear = _.get(dateRange, 'finishDateYear', '')
  const startDateMonthYear = `${startDay} ${startMonth} ${startYear}`
  if (
    startDay === finishDay &&
    startMonth === finishMonth &&
    startYear === finishYear
  ) {
    return startDateMonthYear
  }
  if (finishDate === 'unlimit') {
    return `ไม่จำกัดเวลา`
  }
  return `${startDay} ${startMonth} ${startYear} - ${finishDay} ${finishMonth} ${finishYear}`
}

export const convertDate = (date) => {
  const newdate = date.split('-')
  return `${newdate[2]}/${newdate[1]}/${newdate[0]} `
}

export const handleDownloadSuccess = (res, fileName) => {
  const url = window.URL.createObjectURL(new Blob([res.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
}

export const downloadFileTypePost = createAsyncThunk(
  'downloadFileTypePost',
  async ({ url, body, fileName }, { rejectWithValue }) => {
    try {
      await axios
        .post(url, body, { responseType: 'blob' })
        .then((res) => handleDownloadSuccess(res, fileName))
    } catch (err) {
      return rejectWithValue([], err)
    }
  }
)

export const downloadFileTypeGet = createAsyncThunk(
  'downloadFileTypeGet',
  async ({ url, fileName }, { rejectWithValue }) => {
    try {
      await axios
        .get(url, { responseType: 'blob' })
        .then((res) => handleDownloadSuccess(res, fileName))
    } catch (err) {
      return rejectWithValue([], err)
    }
  }
)

export const checkIdCard = (id) => {
  if (!isNumeric(id)) return false
  if (id.substring(0, 1) === 0) return false
  if (id.length !== 13) return false
  let sum = 0
  for (let i = 0; i < 12; i++) sum += parseFloat(id.charAt(i)) * (13 - i)
  return (11 - (sum % 11)) % 10 === parseFloat(id.charAt(12))
}

export const startTest = (
  uuid,
  trainingPlanUuid,
  type,
  courseTestAndEvaluateDataUuid
) => {
  const eTestLink = `${window.__env__.REACT_APP_APP_URL}/e-testing/${courseTestAndEvaluateDataUuid}?class=${trainingPlanUuid}`
  const evaLink = `${window.__env__.REACT_APP_APP_URL}/e-evaluation/${uuid}?class=${trainingPlanUuid}&eva=${courseTestAndEvaluateDataUuid}
`
  if (type === 'E_TESTING') window.location.href = eTestLink
  else window.location.href = evaLink
}

export const replaceNewLine = (text) => {
  if (_.isEmpty(text)) return null
  return text.replace(/\r\n|\r|\n/g, '\\n')
}

export const parseJwt = (token) => {
  try {
    return JSON.parse(decodeURIComponent(atob(token.split('.')[1])))
  } catch (e) {
    return null
  }
}

export const replaceProtocol = (url) => {
  if (_.isEmpty(url)) return url
  if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) return url
  return `https://${url}`
}

export const isScrolledIntoElement = (elem, fullyInView) => {
  if (_.isEmpty(elem)) return false

  const element = document.documentElement
  const pageTop = element.scrollTop
  const pageBottom = pageTop + window.innerHeight
  const elementTop = elem.offsetTop
  const elementBottom = elementTop + elem.offsetHeight

  if (fullyInView) {
    return pageTop < elementTop && pageBottom > elementBottom
  } else {
    return elementTop <= pageBottom - 10 && elementBottom >= pageTop
  }
}

let saveTimer
export const saveDebounce = (callback) => {
  let debounceTime = 500

  if (saveTimer) {
    clearTimeout(saveTimer)
  }

  saveTimer = setTimeout(() => {
    callback()
  }, debounceTime)
}

export const isValidateDate = (date) => {
  if (date === '0') return false
  if (!date) return false
  return dayjs(date).isValid()
}

export const getGenderFromPrefix = (prefix, genderOptions) => {
  if (!prefix) return ''

  const male = _.find(genderOptions, (ele) => ele.label === 'ช')
  const female = _.find(genderOptions, (ele) => ele.label === 'ญ')

  if (prefix === 'นาย') {
    return male?.value || ''
  }

  if (prefix === 'นาง' || prefix === 'นางสาว') {
    return female?.value || ''
  }

  // อนุมานว่าถ้ามีคำว่า "หญิง" อยู่ใน prefix ถือเป็นเพศหญิง
  if (prefix.includes('หญิง')) {
    return female?.value || ''
  }

  return ''
}

export const getPrefixENFromTH = (prefixTH, en = false) => {
  if (!prefixTH) return ''

  let map = {
    นาย: 'Mr',
    นาง: 'Mrs',
    นางสาว: 'Ms',
  }

  if (en) {
    map = {
      Mr: 'นาย',
      Mrs: 'นาง',
      Ms: 'นางสาว',
    }
  }

  return map[prefixTH] || ''
}

export const convertNumberWithComma = (value) => {
  return parseInt(value || 0).toLocaleString()
}

export const convertDateToThaiString = (value, type = null) => {
  const date = new Date(value)
  const month = MONTH_TH_SHORT_LIST[date.getMonth()]
  const buddistYear = date.getFullYear() + 543
  if (type === 'year') {
    return buddistYear
  }
  return `${date.getDate()} ${month} ${buddistYear}`
}

export const convertDurationToTimeRange = (duration, startDateTime) => {
  const dateTime = dayjs(startDateTime).utc()
  const endTime = dateTime.add(duration, 'second')
  return {
    startTime: convertFormatDateTime({
      value: new Date(startDateTime),
      type: 'timeWithSec',
    }),
    endTime: convertFormatDateTime({
      value: endTime,
      type: 'timeWithSec',
    }),
  }
}

export const convertDateShortTh = (date) => {
  dayjs.extend(buddhistEra)
  dayjs.locale('th')
  return dayjs(date).format('DD MMM BBBB')
}

export const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',')
  const matched = arr[0].match(/:(.*?);/) // NOSONAR
  const mime = _.get(matched, 1, '')
  const bstr = window.atob(arr[arr.length - 1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

export const handleResizeFile = (
  file,
  minWidth,
  minHeight,
  maxWidth = 800,
  maxHeight = 800
) => {
  const fileName = _.get(file, 'name', null)
  const fileType = _.lowerCase(fileName.split('.').pop())
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      fileType,
      100,
      0,
      (uri) => resolve(uri),
      'file',
      minWidth,
      minHeight
    )
  })
}

export function validateNameEN(value, values) {
  const isDMS = _.get(values, 'prospectProfile.isDMS', false)
  if (!/^[a-zA-Z]+$/.test(value) && isDMS) {
    return ''
  }
  return value
}

export const checkManagerInPath = (path) => {
  const regex = /^\/dms\/manager\//
  return regex.test(path)
}

export const checkStartsWithDms = (path) => {
  const regex = /^\/dms/
  return regex.test(path)
}

export const checkStartsWithEvent = (path) => {
  const regex = /^\/event/
  return regex.test(path)
}

export const calculateDateDiffToText = (start, end) => {
  const endDateTime = dayjs(end ? new Date(end) : new Date())
  const formattedStartDate = dayjs(new Date(start)).utc().format('MM/DD/YYYY')
  const formattedStartTime = convertFormatDateTime({
    value: start,
    type: 'time',
  })
  const startDateTime = `${formattedStartDate} ${formattedStartTime}`

  const startDate = dayjs(new Date(startDateTime))
  const minDiff = endDateTime.diff(startDate, 'minutes', true)
  const hourDiff = endDateTime.diff(startDate, 'hours', true)
  const dayDiff = endDateTime.diff(startDate, 'day', true)
  const monthDiff = endDateTime.diff(startDate, 'month', true)
  const yearDiff = endDateTime.diff(startDate, 'year', true)
  const timeObj = { value: '', prefix: '' }

  if (minDiff / 60 < 1) {
    timeObj.value = minDiff
    timeObj.prefix = 'นาที'
  } else if (hourDiff / 24 < 1) {
    timeObj.value = hourDiff
    timeObj.prefix = 'ชั่วโมง'
  } else if (dayDiff / 30 < 1) {
    timeObj.value = dayDiff
    timeObj.prefix = 'วัน'
  } else if (monthDiff / 12 < 1) {
    timeObj.value = monthDiff
    timeObj.prefix = 'เดือน'
  } else {
    timeObj.value = yearDiff
    timeObj.prefix = 'ปี'
  }

  timeObj.value = _.round(timeObj.value)
  return timeObj
}

let socket = null

export const initSocketListener = ({
  onMessage,
  onError,
  onConnect, // ✅ เพิ่ม
  dispatch,
  once = false,
}) => {
  const socketUrl = window?.__env__?.REACT_APP_SOCKET_URL
  if (!socketUrl) return null

  const socket = io(socketUrl, {
    autoConnect: true,
    transports: ['websocket', 'polling'],
    auth: {
      Authorization: localStorage.getItem('token'),
    },
  })

  // คิวสมัครอบรมลดลง 1 ใบ -> ขยับแถบหน้าเข้าแถว
  // ผูกนอก 'connect' เพราะชื่อ event คงที่ ถ้าผูกข้างในทุกครั้งที่ reconnect จะได้ listener เพิ่มอีกตัว
  // แล้วนับคิวซ้ำ (2 เด้งต่อ 1 ใบ) เลขจึงวิ่งเร็วผิดปกติ
  socket.on('ams_queue_enroll', (res) => {
    dispatch?.(tickQueue(res))
  })

  socket.on('connect', () => {
    if (onConnect) onConnect(socket.id) // ✅ เรียก callback พร้อม id

    socket.on(socket.id, (res) => {
      if (res?.success) {
        onMessage?.(res, dispatch)
      } else {
        onError?.(res, dispatch)
      }
      if (once) socket.off(socket.id)
    })
  })

  return socket
}

export const disconnectSocket = () => {
  if (socket && socket.connected) {
    socket.disconnect()
  }
}

export const parseBetween = (between) => {
  if (!between) return { startYear: '-', endYear: '-' }

  const parts = between.split('-').map((s) => s.trim())
  const [start, end] = parts

  if (parts.length === 2) {
    return {
      startYear: start || null,
      endYear: end || null,
    }
  }

  return {
    startYear: parts[0] || null,
    endYear: null,
  }
}

export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )
}

// สร้าง URL ไฟล์จาก S3 key รองรับทั้ง key เก่า (มี '/' นำ) และ key ใหม่ ilearn/... — null-safe
export const fileUrl = (key) => {
  const base = window.__env__.REACT_APP_FILE_URL
  if (!key || typeof key !== 'string') return `${base}/`
  return key.startsWith('/') ? `${base}${key}` : `${base}/${key}`
}

export const normalizeUser = (userData) => {
  if (!userData || typeof userData !== 'object') {
    return userData
  }

  const normalizedUser = _.cloneDeep(userData)

  // เก็บแค่สถานะว่ามีเลขบัตรประชาชนในระบบแล้วหรือยัง (boolean) แทนเลขจริง -
  // EnrollButton ใช้เช็คว่าต้องเด้ง dialog ให้กรอกเลขบัตรก่อนลงทะเบียนไหม
  normalizedUser.hasIdCardNo = !_.isEmpty(userData.idCardNo)

  // Remove unused PII
  _.unset(normalizedUser, 'idCardNo')
  _.unset(normalizedUser, 'agentProfile.agentData')
  // ข้อมูล agent ผู้ดูแล prospect (ซ้อนอยู่ใน prospectProfile) มี PII เต็มชุด
  // ของ agent คนนั้น (idCardNo, dateOfBirth, agentProfile.agentData ฯลฯ) และไม่
  // มีหน้าไหนอ่านค่านี้เลย - ตัดทั้ง object ทิ้งเหมือน agentProfile.agentData
  _.unset(normalizedUser, 'prospectProfile.managerUser')
  _.unset(normalizedUser, 'dateOfBirth')
  _.unset(normalizedUser, 'mobileNo')
  _.unset(normalizedUser, 'address')
  _.unset(normalizedUser, 'province')
  _.unset(normalizedUser, 'amphure')
  _.unset(normalizedUser, 'tambon')
  _.unset(normalizedUser, 'gender')

  _.unset(normalizedUser, 'educational')
  _.unset(normalizedUser, 'qualification')
  _.unset(normalizedUser, 'institution')
  _.unset(normalizedUser, 'branch')
  _.unset(normalizedUser, 'gpa')
  _.unset(normalizedUser, 'between')

  _.unset(normalizedUser, 'firstName')
  _.unset(normalizedUser, 'lastName')
  _.unset(normalizedUser, 'prefix')
  _.unset(normalizedUser, 'prefixTH')

  _.unset(normalizedUser, 'email')
  _.unset(normalizedUser, 'username')

  // Remove unused staff profile data
  _.unset(normalizedUser, 'staffProfile.tel')
  _.unset(normalizedUser, 'staffProfile.email')
  _.unset(normalizedUser, 'staffProfile.birthOfDate')
  _.unset(normalizedUser, 'staffProfile.address')
  _.unset(normalizedUser, 'staffProfile.nationality')
  _.unset(normalizedUser, 'staffProfile.image')
  _.unset(normalizedUser, 'staffProfile.imageFull')
  _.unset(normalizedUser, 'staffProfile.prefix')
  _.unset(normalizedUser, 'staffProfile.firstName')
  _.unset(normalizedUser, 'staffProfile.lastName')
  _.unset(normalizedUser, 'staffProfile.position')
  _.unset(normalizedUser, 'staffProfile.costCenter')
  _.unset(normalizedUser, 'staffProfile.joinDate')
  _.unset(normalizedUser, 'staffProfile.status')
  _.unset(normalizedUser, 'staffProfile.deskNumber')
  _.unset(normalizedUser, 'staffProfile.idCardNo')
  _.unset(normalizedUser, 'staffProfile.educational')
  _.unset(normalizedUser, 'staffProfile.experienced')
  _.unset(normalizedUser, 'staffProfile.training')
  _.unset(normalizedUser, 'staffProfile.lecturer')
  _.unset(normalizedUser, 'staffProfile.institute')
  _.unset(normalizedUser, 'staffProfile.workHistory')
  _.unset(normalizedUser, 'staffProfile.workHistory')
  _.unset(normalizedUser, 'staffProfile.level')

  // Remove unused management fields
  _.unset(normalizedUser, 'createdAt')
  _.unset(normalizedUser, 'updatedAt')
  _.unset(normalizedUser, 'createdBy')
  _.unset(normalizedUser, 'updatedBy')
  _.unset(normalizedUser, 'status')

  // Remove unused reference fields
  _.unset(normalizedUser, 'id')
  _.unset(normalizedUser, 'externalUuid')

  // Remove unused agent data
  _.unset(normalizedUser, 'joinDate')
  _.unset(normalizedUser, 'terminationDate')
  _.unset(normalizedUser, 'point')
  _.unset(normalizedUser, 'userPointHistory')

  _.unset(normalizedUser, 'agentStatusCode')
  _.unset(normalizedUser, 'agentLevelCode')
  _.unset(normalizedUser, 'agentCategory')
  _.unset(normalizedUser, 'RAMCode')
  _.unset(normalizedUser, 'syncDate')

  // Remove unused learner / prospect data
  _.unset(normalizedUser, 'levelOfLearner')
  _.unset(normalizedUser, 'isProfileEditable')
  _.unset(normalizedUser, 'tempProspectProfile')

  return normalizedUser
}
